import { useId, useState, type FormEvent } from 'react';

import { ApiError } from '@/api/client';
import type { Vacancy, VacancyWrite } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select, Textarea } from '@/components/ui/Select';

import { useSaveVacancy } from '../api';
import styles from '../pages/CareersPage.module.css';

const TEXT_FIELDS = [
  { key: 'title', label: 'Job title', required: true, max: 200 },
  { key: 'industry', label: 'Industry', required: true, max: 120 },
  { key: 'country', label: 'Country', required: true, max: 120 },
  { key: 'location', label: 'City or location', required: false, max: 200 },
  { key: 'employment_type', label: 'Employment type', required: true, max: 80 },
  { key: 'salary', label: 'Salary and currency', required: false, max: 200 },
] as const;

export function VacancyForm({
  vacancy,
  onClose,
}: {
  vacancy: Vacancy | null;
  onClose: () => void;
}) {
  const formId = useId();
  const save = useSaveVacancy();
  const [draft, setDraft] = useState<VacancyWrite>(() => ({
    title: vacancy?.title ?? '',
    employer: vacancy?.employer ?? '',
    industry: vacancy?.industry ?? '',
    country: vacancy?.country ?? '',
    location: vacancy?.location ?? '',
    employment_type: vacancy?.employment_type ?? 'Full time',
    salary: vacancy?.salary ?? '',
    highlighted: vacancy?.highlighted ?? false,
    openings: vacancy?.openings ?? 1,
    description: vacancy?.description ?? '',
    requirements: vacancy?.requirements ?? '',
    closing_on: vacancy?.closing_on ?? null,
    state: vacancy?.state ?? 'published',
  }));
  const [changed, setChanged] = useState(false);
  const [discard, setDiscard] = useState(false);
  const change = <K extends keyof VacancyWrite>(key: K, value: VacancyWrite[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setChanged(true);
  };
  const close = () => {
    if (!save.isPending) {
      if (changed) setDiscard(true);
      else onClose();
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({ id: vacancy?.id, values: draft }, { onSuccess: onClose });
  };
  const error = save.error instanceof ApiError ? save.error : null;
  return (
    <Modal
      open
      size="lg"
      title={vacancy ? 'Edit vacancy' : 'Create vacancy'}
      description="Published vacancies are available for staff to share with candidates."
      onClose={close}
      footer={
        discard ? (
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setDiscard(false);
              }}
            >
              Keep editing
            </Button>
            <Button variant="danger" onClick={onClose}>
              Discard changes
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" disabled={save.isPending} onClick={close}>
              Cancel
            </Button>
            <Button type="submit" form={formId} loading={save.isPending}>
              Save vacancy
            </Button>
          </>
        )
      }
    >
      {discard ? <Alert tone="warning">Discard your unsaved vacancy changes?</Alert> : null}
      {save.isError ? (
        <Alert tone="danger">{error?.message ?? 'The vacancy could not be saved.'}</Alert>
      ) : null}
      <form id={formId} onSubmit={submit} className={styles.form}>
        <div className={styles.formGrid}>
          {TEXT_FIELDS.map(({ key, label, required, max }) => (
            <Field
              key={key}
              label={label}
              required={required}
              error={error?.fieldErrors.find((item) => item.field === key)?.message}
            >
              {(ids) => (
                <Input
                  {...ids}
                  required={required}
                  maxLength={max}
                  disabled={save.isPending}
                  value={draft[key]}
                  onChange={(event) => {
                    change(key, event.target.value);
                  }}
                />
              )}
            </Field>
          ))}
          <Field label="Visibility">
            {(ids) => (
              <Select
                {...ids}
                value={draft.state}
                disabled={save.isPending}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'published', label: 'Published' },
                  { value: 'closed', label: 'Inactive' },
                ]}
                onChange={(event) => {
                  change('state', event.target.value as Vacancy['state']);
                }}
              />
            )}
          </Field>
        </div>
        <Checkbox
          label="Highlight on the public Jobs page"
          checked={draft.highlighted}
          disabled={save.isPending}
          onChange={(event) => {
            change('highlighted', event.target.checked);
          }}
        />
        <Field
          label="Job description"
          required
          error={error?.fieldErrors.find((item) => item.field === 'description')?.message}
        >
          {(ids) => (
            <Textarea
              {...ids}
              required
              rows={6}
              maxLength={20000}
              value={draft.description}
              disabled={save.isPending}
              onChange={(event) => {
                change('description', event.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Experience and requirements">
          {(ids) => (
            <Textarea
              {...ids}
              rows={4}
              maxLength={10000}
              value={draft.requirements}
              disabled={save.isPending}
              onChange={(event) => {
                change('requirements', event.target.value);
              }}
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
